import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FlatList, useWindowDimensions } from "react-native";
import { Header } from "../../components/Header";
import { Product } from "../../components/Product";
import { useCart } from "../../hooks/use-cart";
import { NavigationProp, RouteProp, useNavigation } from "@react-navigation/native";
import type { Product as ProductService, Category as CategoryService } from '@inhouse-market/sdk';
import {
	Container,
	Content,
	CategoriesContainer,
	CategoriesContainerContent,
	CategoriesContent,
	TitleCategories,
	TextCategories,
	Products,
	ContainerBtnSearch,
	ButtonContainer,
	TextLoadingCategory,
} from "./styles";
import { BaseClass } from "../../components/BaseClassCategory";
import SearchButton from "../../components/SearchButton";
import { useStore } from "../../hooks/use-store";
import useCategories from "../../store/category/useGetCategory";
import useProducts from "../../store/products/useProducts";
import useStores from '../../store/store/useStore';
import { getProductPricing } from "../../utils/productPricing";

interface Category {
	id: number;
	name: string;
}

interface RouteParams {
	key: string;
	name: string;
	params: PropsCategory;
	path: any;
}

interface PropsCategory {
	categoryId: number
	name: string
}

type Navigation = NavigationProp<any>;

type CategoryRouteProp = RouteProp<Record<string, RouteParams>, 'Category'>;

const Category: React.FC<{ route?: CategoryRouteProp }> = ({ route }) => {
	const { goBack } = useNavigation();
	const navigation = useNavigation<Navigation>();
	const [categoryName, setCategoryName] = useState(route?.params?.params?.name);
	const [categoryId, setCategoryId] = useState(route?.params?.params?.categoryId);
	const [numColumns, setNumColumns] = useState(2);

	const cart = useCart();
	const cartItems = useMemo(() => cart.getCart(), [cart]);
	const paramStore = useStore();
	const dimensions = useWindowDimensions();

	const flatListRef = useRef(null);

	const {
		data: productCategory,
		fetchNextPage: fetchNextPageProduct,
		hasNextPage: hasNextPageProduct,
		isInitialLoading: isInitialLoadingProduct,
		isFetching
	} = useProducts(Number(paramStore.storeId), categoryId, categoryName);

	const {
		data: store,
	} = useStores(Number(paramStore.storeId));

	const {
		data: categoryList,
	} = useCategories(Number(paramStore.storeId));

	const flattenedProducts = useMemo<ProductService.PlanogramItem[]>(() => {
		if (!productCategory) {
			return []
		}
		return productCategory?.pages.flatMap((page) => page.data) || [];
	}, [productCategory]);

	const categoryData = useMemo<CategoryService.CategoryItem[]>(() => {
		if (!categoryList) {
			return []
		}

		return categoryList?.pages.flatMap((page) => page.data) || [];
	}, [categoryList]);

		const amount = cartItems.reduce<Record<number, number>>((sumAmount, product) => {
		sumAmount[product.id] = product.quantity;
		return sumAmount;
	}, {});

	const refreshCategories = async ({
		name,
		categoryId
	}: {
		name: string, categoryId: number
	}) => {
		if (name == categoryName) {
			return goBack();
		}
		setCategoryId(categoryId)
		setCategoryName(name)
	};

	const handleChangeQuantity = useCallback((product: ProductService.PlanogramItem, qnty: number) => {
		const cartItem = cartItems.find((item) => item.id === product.id);
		const actualQnty = !cartItem ? 1 : qnty - cartItem.quantity;
		cart.updateCartItem(product, actualQnty, false, false, false, store as any);
	}, [cart, cartItems, store]);

	const handleNavigateToScreen = useCallback((screenName: string, params: Object | undefined = undefined,) => {
		navigation.navigate(screenName, {
			params,
		});
	}, [navigation]);

	useEffect(() => {
		const productCardWidth = 160;
		const marginOfEachProductCard = 20;
		const cardQuantityPerLine = Math.floor(dimensions.width / (productCardWidth + marginOfEachProductCard))
		setNumColumns(cardQuantityPerLine);
	}, [dimensions])

	const handleIntersection = (entries: IntersectionObserverEntry[]) => {
		const entry = entries[0];
		if (entry.isIntersecting && hasNextPageProduct) {
			fetchNextPageProduct();
		}
	};

	useEffect(() => {
		const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 });

		if (flatListRef.current) {
			observer.observe(flatListRef.current);
		}

		return () => {
			if (flatListRef.current) {
				observer.unobserve(flatListRef.current);
			}
		};
	}, [flatListRef.current]);

	const renderItemProduct = useCallback(({ item }: { item: ProductService.PlanogramItem }) => {
		const pricing = getProductPricing(item);
		return (
			<Product
				key={item.id}
				title={item?.product.name}
				imgUrl={item?.product?.image_url}
				price_list={pricing.priceListInCents / 100}
				quantity={amount[item?.id] || 0}
				maxQuantity={item.stock_amount}
				onQuantityChange={(qtd: number) => handleChangeQuantity(item, qtd)}
				quantityOpen={cart.cartIds.includes(item?.id)}
				price_final={pricing.finalPriceInCents / 100}
				style={{
					marginBottom: 20,
					marginLeft: 20,
				}}
				testID={item.id.toString()}
				onPress={() => handleNavigateToScreen('Product', { product: item })}
			/>
		);
	}, [amount, cart.cartIds, handleChangeQuantity, handleNavigateToScreen]);

	const keyExtractor = useCallback((item: ProductService.PlanogramItem, i: number) => `${i}-${item?.id}`, []);

	return (
		<>
			<Header />
			<Container>
				<Content>
					<CategoriesContainer showsHorizontalScrollIndicator={false}>
						<TitleCategories>Categorias</TitleCategories>
						<CategoriesContainerContent
							horizontal
							showsHorizontalScrollIndicator={false}
						>
							{categoryData?.length && categoryData.map((item: { id: number, name: string }) => {
								return (
									<CategoriesContent
										key={item.id.toString()}
										selected={item.name == categoryName}
										onPress={() =>
											refreshCategories({
												name: item.name,
												categoryId: item.id
											})
										}
									>
										<TextCategories
											selected={item.name == categoryName}
										>
											{item.name}
										</TextCategories>
									</CategoriesContent>
								);
							})}
						</CategoriesContainerContent>

						<Products>
							<FlatList
								ListHeaderComponent={() => (
									!isInitialLoadingProduct ? <BaseClass title={categoryName || ""} /> : null
								)}
								data={flattenedProducts || []}
								key={numColumns}
								extraData={cart?.cartIds}
								keyExtractor={keyExtractor}
								renderItem={renderItemProduct}
								numColumns={numColumns}
								showsVerticalScrollIndicator={false}
								ListFooterComponent={() => (
									<ButtonContainer ref={flatListRef}>
										{isInitialLoadingProduct && (
											<TextLoadingCategory>Carregando Produtos...</TextLoadingCategory>
										)}
									</ButtonContainer>
								)}
							/>
						</Products>
						{isFetching && !isInitialLoadingProduct && (
							<ButtonContainer ref={flatListRef}>
								<TextLoadingCategory>
									Carregando Produtos...
								</TextLoadingCategory>
							</ButtonContainer>
						)}

					</CategoriesContainer>
				</Content>
				<ContainerBtnSearch>
					<SearchButton />
				</ContainerBtnSearch>
			</Container>
		</>
	);
};

export default Category;
